/**
 * Utility function to convert markdown content to human-readable plain text
 * Removes markdown syntax while preserving the text content
 */

export function markdownToPlainText(markdown: string): string {
  if (!markdown) return '';

  let text = markdown;

  // Remove code blocks (```code```)
  text = text.replace(/```[\s\S]*?```/g, '');

  // Remove inline code (`code`)
  text = text.replace(/`([^`]+)`/g, '$1');

  // Remove markdown links but keep the text: [text](url) -> text, [text]() -> text
  text = text.replace(/\[([^\]]+)\]\([^\)]*\)/g, '$1');

  // Remove markdown images: ![alt](url) -> alt, ![alt]() -> alt
  text = text.replace(/!\[([^\]]*)\]\([^\)]*\)/g, '$1');

  // Clean up any remaining empty parentheses patterns that might be artifacts
  // Handle possessive forms with empty links: Coorg's[Coorg]() -> Coorg's() -> Coorg's
  text = text.replace(/(\w+)'s\(\)/g, "$1's");
  // Handle word followed by empty parentheses (likely leftover from link processing)
  text = text.replace(/([a-zA-Z])\(\)/g, '$1');

  // Remove markdown headings (###, ##, #) but keep the text
  text = text.replace(/^#{1,6}\s+(.+)$/gm, '$1');

  // Remove markdown bold (**text** or __text__) but keep the text
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/__([^_]+)__/g, '$1');

  // Remove markdown italic (*text* or _text_) but keep the text
  text = text.replace(/\*([^*]+)\*/g, '$1');
  text = text.replace(/_([^_]+)_/g, '$1');

  // Remove markdown strikethrough (~~text~~) but keep the text
  text = text.replace(/~~([^~]+)~~/g, '$1');

  // Convert markdown list items to plain text with proper spacing
  // Handle bullet points (*, -, •) - keep the text, remove the marker
  text = text.replace(/^[\s]*[*•-]\s+(.+)$/gm, '• $1');

  // Handle numbered lists (1., 2., etc.) - keep the text, remove the number
  text = text.replace(/^\s*\d+\.\s+(.+)$/gm, '$1');

  // Remove horizontal rules (---, ***, ___)
  text = text.replace(/^[-*_]{3,}$/gm, '');

  // Remove blockquotes (> text) but keep the text
  text = text.replace(/^>\s+(.+)$/gm, '$1');

  // Clean up multiple consecutive newlines (more than 2)
  text = text.replace(/\n{3,}/g, '\n\n');

  // Clean up leading/trailing whitespace on each line
  text = text
    .split('\n')
    .map(line => line.trim())
    .join('\n');

  // Remove leading/trailing whitespace from the entire text
  text = text.trim();

  return text;
}

/**
 * Formats a conversation for copying with proper user/assistant labels
 */
export function formatConversationForCopy(
  messages: Array<{ role: string; content: string }>
): string {
  return messages
    .map(message => {
      const role = message.role === 'user' ? 'You' : 'Assistant';
      const plainText = markdownToPlainText(message.content);
      return `${role}: ${plainText}`;
    })
    .join('\n\n');
}
